import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const getFileAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "multipart/form-data",
});

// ==================== KANBAN BOARDS ====================

export const fetchKanbanBoards = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.salesPerson) params.append("salesPerson", options.salesPerson);
  if (options.search) params.append("search", options.search);
  const queryString = params.toString();
  const response = await axios.get(
    `${API_BASE_URL}/boards${queryString ? `?${queryString}` : ""}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const fetchKanbanBoardDetail = async (boardId) => {
  const response = await axios.get(`${API_BASE_URL}/boards/${boardId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

/**
 * Admin-only board dashboard (KPIs, charts, top tables).
 * GET /crm/boards/:boardId/dashboard — matches backend CRM route mount.
 */
export const fetchCrmBoardDashboard = async (boardId, options = {}) => {
  const params = new URLSearchParams();
  if (Array.isArray(options.tags) && options.tags.length) {
    params.append("tags", options.tags.join(","));
  }
  if (options.category) params.append("category", options.category);
  if (options.salesPerson) params.append("salesPerson", options.salesPerson);
  if (options.leadSource) params.append("leadSource", options.leadSource);
  if (options.dateFrom) params.append("dateFrom", options.dateFrom);
  if (options.dateTo) params.append("dateTo", options.dateTo);

  const queryString = params.toString();
  const response = await axios.get(
    `${API_BASE_URL}/boards/${boardId}/dashboard${
      queryString ? `?${queryString}` : ""
    }`,
    { headers: getAuthHeaders() },
  );
  return response.data;
};

export const createKanbanBoard = async (boardData) => {
  const response = await axios.post(`${API_BASE_URL}/boards`, boardData, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const updateKanbanBoard = async (boardId, boardData) => {
  const response = await axios.patch(
    `${API_BASE_URL}/boards/${boardId}`,
    boardData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deleteKanbanBoard = async (boardId) => {
  const response = await axios.delete(`${API_BASE_URL}/boards/${boardId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// ==================== STAGES / COLUMNS ====================

export const createStage = async (boardId, stageData) => {
  const response = await axios.post(
    `${API_BASE_URL}/boards/${boardId}/stages`,
    stageData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getStagesByBoardId = async (boardId) => {
  const response = await axios.get(`${API_BASE_URL}/boards/${boardId}/stages`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const updateStage = async (boardId, stageId, stageData) => {
  const response = await axios.patch(
    `${API_BASE_URL}/boards/${boardId}/stages/${stageId}`,
    stageData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deleteStage = async (boardId, stageId) => {
  const response = await axios.delete(
    `${API_BASE_URL}/boards/${boardId}/stages/${stageId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Note: backend route order means /stages/reorder must be invoked carefully;
// kept here for completeness. Optimized to move only one stage at a time.
export const reorderStages = async (boardId, fromStageId, toStageId, side) => {
  const response = await axios.patch(
    `${API_BASE_URL}/boards/${boardId}/stages/reorder`,
    { fromStageId, toStageId, side },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ==================== LEADS ====================

export const fetchLeads = async (boardId, options = {}) => {
  const params = new URLSearchParams();

  if (options.search) params.append("search", options.search);

  if (options.category) params.append("category", options.category);
  if (Array.isArray(options.tags) && options.tags.length)
    params.append("tags", options.tags.join(","));
  if (options.status) params.append("status", options.status);
  if (options.priority) params.append("priority", options.priority);
  if (options.leadSource) params.append("leadSource", options.leadSource);
  if (options.assignedTo) params.append("assignedTo", options.assignedTo);
  if (options.salesPerson) params.append("salesPerson", options.salesPerson);
  if (options.columnId) params.append("columnId", options.columnId);
  if (options.stageId) params.append("stageId", options.stageId);

  if (options.sortBy) params.append("sortBy", options.sortBy);
  if (options.sortOrder) params.append("sortOrder", options.sortOrder);

  if (options.limit) params.append("limit", options.limit);
  if (options.page) params.append("page", options.page);

  const queryString = params.toString();
  const response = await axios.get(
    `${API_BASE_URL}/boards/${boardId}/leads${
      queryString ? `?${queryString}` : ""
    }`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const fetchLeadDetail = async (boardId, leadId) => {
  const response = await axios.get(
    `${API_BASE_URL}/boards/${boardId}/leads/${leadId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const moveLeadToStage = async (boardId, leadId, stageId, position) => {
  const response = await axios.patch(
    `${API_BASE_URL}/boards/${boardId}/leads/${leadId}/move`,
    { stageId, position },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const updateLead = async (boardId, leadId, leadData) => {
  const response = await axios.patch(
    `${API_BASE_URL}/boards/${boardId}/leads/${leadId}`,
    leadData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const addLeadToBoard = async (boardId, leadData) => {
  const response = await axios.post(
    `${API_BASE_URL}/boards/${boardId}/leads`,
    leadData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const removeLeadFromBoard = async (boardId, leadId) => {
  const response = await axios.delete(
    `${API_BASE_URL}/boards/${boardId}/leads/${leadId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ==================== NOTES & ACTIVITIES ====================

export const addNoteToLead = async (boardId, leadId, noteData) => {
  const response = await axios.post(
    `${API_BASE_URL}/boards/${boardId}/leads/${leadId}/notes`,
    noteData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const fetchLeadNotes = async (boardId, leadId) => {
  const response = await axios.get(
    `${API_BASE_URL}/boards/${boardId}/leads/${leadId}/notes`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deleteNote = async (boardId, leadId, noteId) => {
  const response = await axios.delete(
    `${API_BASE_URL}/boards/${boardId}/leads/${leadId}/notes/${noteId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const fetchLeadActivities = async (boardId, leadId) => {
  const response = await axios.get(
    `${API_BASE_URL}/boards/${boardId}/leads/${leadId}/activities`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ==================== IMPORT / EXPORT ====================

export const importLeads = async (boardId, file) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await axios.post(
    `${API_BASE_URL}/boards/${boardId}/import/excel`,
    formData,
    { headers: getFileAuthHeaders() }
  );
  return response.data;
};

export const exportBoardExcel = async (boardId) => {
  const response = await axios.get(
    `${API_BASE_URL}/boards/${boardId}/export/excel`,
    {
      headers: getAuthHeaders(),
      responseType: "blob",
    }
  );
  return response;
};

export const exportBoardPDF = async (boardId) => {
  const response = await axios.get(
    `${API_BASE_URL}/boards/${boardId}/export/pdf`,
    {
      headers: getAuthHeaders(),
      responseType: "blob",
    }
  );
  return response;
};

// ==================== TAGS ====================

export const fetchAvailableTags = async (boardId) => {
  const response = await axios.get(`${API_BASE_URL}/boards/${boardId}/tags`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const addTagToLead = async (boardId, leadId, tagData) => {
  const response = await axios.post(
    `${API_BASE_URL}/boards/${boardId}/leads/${leadId}/tags`,
    tagData,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const removeTagFromLead = async (boardId, leadId, tag) => {
  const response = await axios.delete(
    `${API_BASE_URL}/boards/${boardId}/leads/${leadId}/tags/${encodeURIComponent(
      tag
    )}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ==================== BULK OPERATIONS ====================

export const bulkMoveLeads = async (boardId, leadIds, stageId) => {
  const response = await axios.patch(
    `${API_BASE_URL}/boards/${boardId}/leads/bulk-move`,
    { leadIds, stageId },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const bulkAddTags = async (boardId, leadIds, tags) => {
  const response = await axios.patch(
    `${API_BASE_URL}/boards/${boardId}/leads/bulk-tags`,
    { leadIds, tags },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const bulkDeleteLeads = async (boardId, leadIds) => {
  const response = await axios.patch(
    `${API_BASE_URL}/boards/${boardId}/leads/bulk-delete`,
    { leadIds },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ==================== STUDENTS / CONSULTANTS (for lead pickers) ====================

export const fetchAllStudents = async () => {
  const response = await axios.get(`${API_BASE_URL}/student/all`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const fetchAllConsultants = async () => {
  const response = await axios.get(`${API_BASE_URL}/consultant/all`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const fetchConsultantsWithSalesRole = async () => {
  const response = await axios.get(`${API_BASE_URL}/consultant/all/details`, {
    headers: getAuthHeaders(),
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  return list
    .filter((c) => c.hasSalesPersonRole === true)
    .map((c) => ({ ...c, _id: c._id || c.id }));
};

export const updateConsultantBoardAssignments = async (consultantId, assignedBoardIds) => {
  const response = await axios.patch(
    `${API_BASE_URL}/admin/consultants/${consultantId}/sales-role`,
    { hasSalesPersonRole: true, assignedBoardIds },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ==================== UTILITIES ====================

export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const handleExportDownload = (response, format) => {
  const timestamp = new Date().toISOString().split("T")[0];
  const filename = `crm-board-export-${timestamp}.${format}`;
  downloadFile(response.data, filename);
};
